import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { useInvoicesDataTable } from 'generated/resources/datatable.graphql'
import {
  AvailableCurrencyFragment,
  ContactFragment,
  FinanceValueObjectsFinancialDocumentOperationTypeEnum,
  FinanceValueObjectsFinancialDocumentTypeEnum,
  FinanceValueObjectsInvoiceStatusEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import useSearchParams from 'modules/core/hooks/useSearchParams'

import InvoicesDataTable from 'modules/finance/components/Invoices/DataTable'
import { InvoicesUploadModal } from 'modules/finance/components/InvoicesUploadModal'

import { DefaultCurrency } from './constants'
import { invoicesDataTableProjections } from './projections'
import { InvoiceFilters } from './types'

interface Props {
  vendor?: ContactFragment
  currencies: AvailableCurrencyFragment[]
  financialDocumentsPaymentsAvailable: boolean
  legalEntities: { label: string; value: string }[]
  canCreatePurchaseFinancialDocuments: boolean
}

const DOCUMENT_TYPES: FinanceValueObjectsFinancialDocumentTypeEnum[] = ['invoice', 'credit_note']
const OPERATION_TYPES: FinanceValueObjectsFinancialDocumentOperationTypeEnum[] = ['purchase']
const DEFAULT_TAB = 'all'
const REVIEW_TAB = 'review'

const TAB_TO_STATUS_MAP: Record<string, FinanceValueObjectsInvoiceStatusEnum | undefined> = {
  all: undefined,
  review: 'review',
  sent_to_pay: 'sent_to_pay',
  paid: 'paid',
}

const DataTableWrapper = ({
  vendor,
  currencies,
  financialDocumentsPaymentsAvailable,
  legalEntities,
  canCreatePurchaseFinancialDocuments,
}: Props) => {
  const { access, company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { validated } = useSearchParams<{ validated?: boolean }>()

  const [tab, setTab] = useState<string>(DEFAULT_TAB)
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false)
  const [filters, setFilters] = useState<InvoiceFilters>({})

  const { debouncedSearch, search, onSearch } = useDebouncedSearch()

  const mainLegalEntity = useMemo(
    () => company.legalEntities.find((el) => el.main),
    [company.legalEntities]
  )

  const defaultCurrency = useMemo(
    () => mainLegalEntity?.currency ?? DefaultCurrency,
    [mainLegalEntity?.currency]
  )

  const projections = useMemo(
    () =>
      invoicesDataTableProjections({
        locale: access.locale,
        skipVendor: Boolean(vendor),
      }),
    [access.locale, vendor]
  )

  useEffect(() => {
    setFilters((current) => ({ ...current, vendorSearch: debouncedSearch }))
  }, [debouncedSearch])

  useEffect(() => {
    if (validated !== undefined) {
      setFilters((current) => ({ ...current, validated }))
    }
  }, [validated])

  const source = useInvoicesDataTable(projections, {
    variables: {
      companyId: company.id,
      documentTypes: DOCUMENT_TYPES,
      operationTypes: OPERATION_TYPES,
      search: debouncedSearch,
      vendorId: vendor?.id,
    },
  })

  const handleTabChange = useCallback((tabParam: string) => {
    setTab(tabParam)

    const statusValue = TAB_TO_STATUS_MAP[tabParam]
    if (statusValue === undefined) {
      // Clear status filter when "all" tab is selected
      setFilters((current) => ({ ...current, status: undefined }))
    } else {
      setFilters((current) => ({ ...current, status: [statusValue] }))
    }
  }, [])

  const handleFilterChange = useCallback((newFilters: InvoiceFilters) => {
    setFilters((current) => ({ ...current, ...newFilters }))
  }, [])

  const handleUploadClick = useCallback(() => {
    setShowUploadModal(true)
  }, [])

  const handleUploadModalClose = useCallback(() => {
    setShowUploadModal(false)
    setTab(REVIEW_TAB)
  }, [])

  const handleExportClick = useCallback(() => {
    navigate(navigation.finance.invoices.list.exporter.fullPath())
  }, [navigate, navigation.finance.invoices.list.exporter])

  const shouldShowUploadButton = canCreatePurchaseFinancialDocuments
  const shouldShowUploadModal = showUploadModal

  return (
    <>
      {shouldShowUploadModal && (
        <InvoicesUploadModal onClose={handleUploadModalClose} vendorId={vendor?.id} />
      )}

      <Box flexDirection="column" gap="s16">
        <InvoicesDataTable
          tab={tab}
          search={search}
          source={source}
          filters={filters}
          legalEntitiesOptions={legalEntities}
          financialDocumentsPaymentsAvailable={financialDocumentsPaymentsAvailable}
          currency={defaultCurrency}
          availableCurrencies={currencies}
          locale={access.locale}
          vendor={vendor}
          onSearch={onSearch}
          onTabChange={handleTabChange}
          onFilterChange={handleFilterChange}
          onUploadClick={shouldShowUploadButton ? handleUploadClick : undefined}
          onExportClick={handleExportClick}
        />
      </Box>
    </>
  )
}

export default DataTableWrapper
